import { useState, useEffect, useCallback, useRef } from 'react';
import { draftsApi } from '../utils/api';

// Check if user is authenticated via JWT token
const isAuthenticated = () => !!localStorage.getItem('token');

// Constants for hybrid draft configuration
const LOCAL_AUTOSAVE_INTERVAL = 10000; // 10 seconds - for crash protection
const SERVER_SAVE_INTERVAL = 60000; // 60 seconds - for server persistence
const DRAFT_EXPIRY_DAYS = 7;
const LOCAL_STORAGE_KEY = 'florist-local-draft';
const LOCAL_STORAGE_EVENT_KEY = 'florist-local-draft-sync';

// Utility functions for localStorage operations (crash protection only)
const getLocalDraft = () => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

const saveLocalDraft = (formData) => {
  try {
    const draft = {
      data: formData,
      timestamp: Date.now(),
      id: 'local_temp_draft'
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(draft));
    window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_EVENT_KEY, { detail: draft }));
    return true;
  } catch (error) {
    return false;
  }
};

const clearLocalDraft = () => {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_EVENT_KEY, { detail: null }));
    return true;
  } catch (error) {
    return false;
  }
};

// Check if form data has meaningful content (exclude default values)
const hasContent = (data) => {
  if (!data || typeof data !== 'object') return false;

  // Only check fields that represent actual user input, not defaults
  const checkFields = ['customerName', 'customerEmail', 'customerPhone', 'items', 'specialInstructions'];

  return checkFields.some(field => {
    const value = data[field];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value && value.toString().trim().length > 0;
  });
};

// Custom hook for hybrid draft management
export const useHybridDrafts = (formData, options = {}) => {
  const {
    onSave = () => {},
    onLoad = () => {},
    onError = () => {},
    enabled = true,
    manualOnly = true, // Only manual saves, no auto-loading/saving
    localAutoInterval = LOCAL_AUTOSAVE_INTERVAL,
    serverSaveInterval = SERVER_SAVE_INTERVAL
  } = options;

  // Local state - MUST call all hooks unconditionally to follow Rules of Hooks
  const [serverDrafts, setServerDrafts] = useState([]);
  const [localDraft, setLocalDraft] = useState(null);
  const [lastLocalSaved, setLastLocalSaved] = useState(null);
  const [lastServerSaved, setLastServerSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [loading, setLoading] = useState(false);

  // Refs for timers and tracking
  const localAutoSaveTimerRef = useRef(null);
  const serverSaveTimerRef = useRef(null);
  const lastFormDataRef = useRef(null);


  // Load initial data - Disabled for manual-only mode
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    if (manualOnly) {
      // In manual-only mode, load both local data and server drafts but don't auto-load forms
      const localData = getLocalDraft();
      if (localData) {
        setLocalDraft(localData);
      }

      // Only load server drafts if authenticated (JWT token or POS mode)
      if (isAuthenticated()) {
        const loadManualServerDrafts = async () => {
          try {
            const response = await draftsApi.getAll({});
            if (response.success) {
              setServerDrafts(response.data.drafts || []);
            }
          } catch (error) {
            // Silently handle authentication errors in manual-only mode
            if (error.status === 401 || error.status === 403) {
              setServerDrafts([]);
            } else {
              onError(error);
            }
          } finally {
            setLoading(false);
          }
        };
        loadManualServerDrafts();
      } else {
        // Not authenticated, no server drafts
        setServerDrafts([]);
        setLoading(false);
      }

      return;
    }

    const loadInitialData = async () => {
      setLoading(true);
      try {
        // Load local crash-protection draft
        const localData = getLocalDraft();
        if (localData) {
          setLocalDraft(localData);
        }

        // In manual-only mode, skip server draft loading to avoid loops
        if (!manualOnly) {
          // Load server drafts - this code path won't execute in current setup
          const response = await draftsApi.getAll({});
          if (response.success) {
            setServerDrafts(response.data.drafts || []);
          }
        }
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [enabled, manualOnly]);

  // Cross-tab synchronization for local drafts
  useEffect(() => {
    if (!enabled) return;

    const handleLocalStorageSync = (event) => {
      if (event.type === LOCAL_STORAGE_EVENT_KEY) {
        setLocalDraft(event.detail);
      }
    };

    const handleStorageChange = (event) => {
      if (event.key === LOCAL_STORAGE_KEY) {
        const localData = getLocalDraft();
        setLocalDraft(localData);
      }
    };

    window.addEventListener(LOCAL_STORAGE_EVENT_KEY, handleLocalStorageSync);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(LOCAL_STORAGE_EVENT_KEY, handleLocalStorageSync);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [enabled]);

  // Load server drafts
  const loadServerDrafts = useCallback(async (filters = {}) => {
    if (!enabled) {
      return { drafts: [], pagination: {} };
    }

    // Check authentication before attempting server requests
    if (!isAuthenticated()) {
      return { drafts: [], pagination: {} };
    }

    try {
      const response = await draftsApi.getAll(filters);
      if (response.success) {
        setServerDrafts(response.data.drafts || []);
        return response.data;
      }
    } catch (error) {
      // Silently handle authentication errors
      if (error.status === 401 || error.status === 403) {
        setServerDrafts([]);
        return { drafts: [], pagination: {} };
      }
      onError(error);
      return { drafts: [], pagination: {} };
    }
  }, [enabled, onError]);

  // Save to server
  const saveToServer = useCallback(async (draftData, notes = null, draftId = null) => {
    if (!enabled) {
      return { success: false, reason: 'Disabled' };
    }

    if (!hasContent(draftData)) {
      return { success: false, reason: 'No content to save' };
    }

    // Check authentication before attempting server requests
    if (!isAuthenticated()) {
      return { success: false, reason: 'Authentication required' };
    }

    setIsSaving(true);
    setSaveStatus('saving');

    try {
      let response;
      if (draftId) {
        response = await draftsApi.update(draftId, draftData, notes);
      } else {
        response = await draftsApi.create(draftData, notes);
      }

      if (response.success) {
        setLastServerSaved(Date.now());
        setSaveStatus('saved');
        onSave(response.data);

        // Refresh server drafts list
        await loadServerDrafts();

        // Reset status after 3 seconds
        setTimeout(() => setSaveStatus('idle'), 3000);

        return { success: true, draft: response.data };
      } else {
        throw new Error(response.message || 'Failed to save draft');
      }
    } catch (error) {
      setSaveStatus('error');
      onError(error);
      setTimeout(() => setSaveStatus('idle'), 3000);
      return { success: false, error: error.message };
    } finally {
      setIsSaving(false);
    }
  }, [enabled, onSave, onError, loadServerDrafts]);

  // Load draft from server
  const loadServerDraft = useCallback(async (draftId) => {
    if (!enabled) {
      return { success: false, reason: 'Disabled' };
    }

    // Check authentication before attempting server requests
    if (!isAuthenticated()) {
      return { success: false, reason: 'Authentication required' };
    }

    try {
      const response = await draftsApi.getById(draftId);
      if (response.success) {
        onLoad(response.data.form_data, response.data);
        return { success: true, draft: response.data };
      } else {
        throw new Error('Draft not found');
      }
    } catch (error) {
      onError(error);
      return { success: false, error: error.message };
    }
  }, [enabled, onLoad, onError]);

  // Delete server draft
  const deleteServerDraft = useCallback(async (draftId) => {
    if (!enabled) {
      return { success: false, reason: 'Disabled' };
    }

    // Check authentication before attempting server requests
    if (!isAuthenticated()) {
      return { success: false, reason: 'Authentication required' };
    }

    try {
      const response = await draftsApi.delete(draftId);
      if (response.success) {
        await loadServerDrafts(); // Refresh list
        return { success: true };
      } else {
        throw new Error(response.message || 'Failed to delete draft');
      }
    } catch (error) {
      onError(error);
      return { success: false, error: error.message };
    }
  }, [enabled, onError, loadServerDrafts]);

  // Load local draft (crash recovery)
  const loadLocalDraft = useCallback(() => {
    if (!enabled) {
      return { success: false, reason: 'Disabled' };
    }

    if (localDraft && hasContent(localDraft.data)) {
      onLoad(localDraft.data, { ...localDraft, isLocal: true });
      return { success: true, draft: localDraft };
    }
    return { success: false, reason: 'No local draft available' };
  }, [enabled, localDraft, onLoad]);

  // Clear local draft
  const clearLocal = useCallback(() => {
    if (!enabled) return;

    clearLocalDraft();
    setLocalDraft(null);
  }, [enabled]);

  // Auto-save to localStorage (crash protection) - DISABLED
  useEffect(() => {
    if (!enabled) return;

    // All auto-save functionality disabled
  }, [enabled]);

  // Auto-save to server (less frequent) - DISABLED
  useEffect(() => {
    if (!enabled) return;

    // All server auto-save functionality disabled
  }, [enabled]);

  // Save on window blur and beforeunload - DISABLED
  useEffect(() => {
    if (!enabled) return;

    // All window event functionality disabled
  }, [enabled]);

  // Get draft counts
  const getDraftCounts = useCallback(() => {
    if (!enabled) {
      return { server: 0, local: 0, total: 0 };
    }

    return {
      server: serverDrafts.length,
      local: localDraft ? 1 : 0,
      total: serverDrafts.length + (localDraft ? 1 : 0)
    };
  }, [enabled, serverDrafts, localDraft]);

  // Check for crash recovery
  const hasLocalRecovery = useCallback(() => {
    if (!enabled || !localDraft) return false;

    const timeSinceLastSave = Date.now() - localDraft.timestamp;
    // Show recovery if local draft is less than 1 hour old AND has actual content
    return timeSinceLastSave < 60 * 60 * 1000 && hasContent(localDraft.data);
  }, [enabled, localDraft]);

  return {
    // State
    serverDrafts,
    localDraft,
    lastLocalSaved,
    lastServerSaved,
    isSaving,
    saveStatus,
    loading,

    // Server draft actions
    saveToServer,
    loadServerDraft,
    deleteServerDraft,
    loadServerDrafts,

    // Local draft actions
    loadLocalDraft,
    clearLocal,

    // Utilities
    getDraftCounts,
    hasLocalRecovery,
    hasContent: enabled ? hasContent(formData) : false
  };
};

export default useHybridDrafts;